import { useEffect, useMemo, useState } from "react";
import actions from "../config.json";
import passionsMap from "../pages/OffersLandingPage/generatedPassionMap.json";

const sampleUnique = (items, count) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const limit = Math.min(Math.max(count, 0), items.length);
  const pool = items.slice();

  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = pool[i];
    pool[i] = pool[j];
    pool[j] = temp;
  }

  return pool.slice(0, limit);
};

/**
 * Hook to fetch hotel data for the Passions Hub using UXL queries.
 * This provides richer hotel data including reviews and ratings.
 *
 * @param {string[]} passions - Array of passion IDs to fetch hotels for
 * @param {Object} options - Additional options
 * @param {number} options.limit - Maximum number of hotels per passion (default: 12)
 * @param {string} options.recentlyViewedPropertyId - Property ID from CDP for "Pick up where you left off"
 * @returns {Object} - { data, loading, error, recentlyViewedProperty }
 */
export const usePassionsHubHotels = (passions, options = {}) => {
  const { limit = 12, recentlyViewedPropertyId = null } = options;
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const passionKey = useMemo(() => {
    if (!Array.isArray(passions) || passions.length === 0) {
      return "";
    }

    return passions
      .map((passion) => String(passion || "").trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join(",");
  }, [passions]);

  // Create a stable cache key that includes the recently viewed property
  const cacheKey = useMemo(() => {
    return `${passionKey}:${recentlyViewedPropertyId || ""}`;
  }, [passionKey, recentlyViewedPropertyId]);

  useEffect(() => {
    let isCancelled = false;

    const fetchHotels = async () => {
      // If no passions and no recently viewed property, nothing to fetch
      if (!passionKey && !recentlyViewedPropertyId) {
        setData(null);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const passionsList = passionKey.split(",").filter(Boolean);
        const selectedProperties = [];
        const passionPropertiesMap = {};

        // Select random properties for each passion locally
        passionsList.forEach((passionId) => {
          const propertyPool = passionsMap[passionId] || [];
          const selected = sampleUnique(propertyPool, limit);
          passionPropertiesMap[passionId] = selected;
          selectedProperties.push(...selected);
        });

        const passionsUrl =
          actions["marriott-passions-v3/get-passions-hub-hotels"];

        if (!passionsUrl) {
          throw new Error("Passions Hub Hotels action URL not configured");
        }

        const requestBody = {
          // Don't send passions to backend, send selected property IDs instead
          propertyIds: selectedProperties,
          limit,
        };

        // Include recently viewed property ID if provided
        if (recentlyViewedPropertyId) {
          requestBody.recentlyViewedPropertyId = recentlyViewedPropertyId;
        }

        const response = await fetch(passionsUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const payload = result.body !== undefined ? result.body : result;


        // Reconstruct the passions structure that the component expects
        // since the backend no longer returns it when we don't send passions
        if (!payload.passions || payload.passions.length === 0) {
          payload.passions = passionsList.map((id) => ({
            id,
            properties: passionPropertiesMap[id] || [],
          }));
        }

        if (isCancelled) {
          return;
        }

        setData(payload);
      } catch (err) {
        if (isCancelled) {
          return;
        }

        console.error("Error fetching passions hub hotels:", err);
        setError(err.message);
        setData(null);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchHotels();

    return () => {
      isCancelled = true;
    };
  }, [cacheKey, passionKey, limit, recentlyViewedPropertyId]);

  // Extract recently viewed property from the response
  const recentlyViewedProperty = data?.recentlyViewedProperty || null;

  return {
    data,
    loading,
    error,
    recentlyViewedProperty,
  };
};
